import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  ArrowLeftIcon,
  BanknotesIcon,
  CreditCardIcon,
  DocumentTextIcon,
  ArrowTrendingUpIcon,
  CalendarDaysIcon,
  UserIcon,
  ReceiptPercentIcon,
  PrinterIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

const PaymentForm = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEditing = Boolean(id);

  // Form state
  const [formData, setFormData] = useState({
    memberId: '',
    amount: '',
    paymentMethod: 'cash',
    description: '',
    paymentDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    category: 'subscription',
    reference: '',
    notes: '',
    discount: 0,
    tax: 0,
    status: 'paid',
    // Receipt details
    receiptNumber: '',
    issueReceipt: true,
  });

  const [showReceipt, setShowReceipt] = useState(false);
  const [generatedPayment, setGeneratedPayment] = useState(null);

  // Fetch members
  const { data: membersData } = useQuery('members', async () => {
    const response = await axios.get('/members');
    return response.data;
  });

  // Fetch existing payment for editing
  const { data: editData } = useQuery(
    ['payment', id],
    async () => {
      const response = await axios.get(`/payments/${id}`);
      return response.data;
    },
    { enabled: isEditing }
  );

  // Create/Update payment mutation
  const createPaymentMutation = useMutation(
    (data) => isEditing ? axios.put(`/payments/${id}`, data) : axios.post('/payments', data),
    {
      onSuccess: (response) => {
        toast.success(`Paiement ${isEditing ? 'mis à jour' : 'créé'} avec succès!`);
        queryClient.invalidateQueries('payments');
        
        if (!isEditing && formData.issueReceipt) {
          setGeneratedPayment(response.data.payment);
          setShowReceipt(true);
        } else {
          navigate('/payments');
        }
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors du paiement');
      },
    }
  );

  // Generate receipt number
  useEffect(() => {
    if (!isEditing && !formData.receiptNumber) {
      const receiptNumber = 'REC-' + Date.now().toString().slice(-6);
      setFormData(prev => ({ ...prev, receiptNumber }));
    }
  }, [isEditing, formData.receiptNumber]);

  // Update form data when editing
  useEffect(() => {
    if (editData) {
      setFormData(prevData => ({
        ...prevData,
        ...editData,
        paymentDate: editData.paymentDate ? editData.paymentDate.split('T')[0] : '',
        dueDate: editData.dueDate ? editData.dueDate.split('T')[0] : '',
      }));
    }
  }, [editData]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.memberId || !formData.amount) {
      toast.error('Veuillez remplir tous les champs requis');
      return;
    }

    const cleanedData = {
      ...formData,
      amount: parseFloat(formData.amount),
      discount: parseFloat(formData.discount) || 0,
      tax: parseFloat(formData.tax) || 0,
    };

    createPaymentMutation.mutate(cleanedData);
  };

  const calculateTotal = () => {
    const amount = parseFloat(formData.amount) || 0;
    const discount = parseFloat(formData.discount) || 0;
    const tax = parseFloat(formData.tax) || 0;
    
    const subtotal = amount - discount;
    const total = subtotal + tax;
    
    return {
      subtotal,
      total,
      discount,
      tax,
    };
  };

  const handlePrintReceipt = () => {
    window.print();
  };

  const paymentMethods = [
    { id: 'cash', name: 'Espèces', icon: BanknotesIcon, color: 'bg-green-500' },
    { id: 'card', name: 'Carte Bancaire', icon: CreditCardIcon, color: 'bg-blue-500' },
    { id: 'transfer', name: 'Virement Bancaire', icon: ArrowTrendingUpIcon, color: 'bg-purple-500' },
    { id: 'check', name: 'Chèque', icon: DocumentTextIcon, color: 'bg-orange-500' },
  ];

  const categories = [
    { id: 'subscription', name: 'Abonnement' },
    { id: 'equipment', name: 'Équipement' },
    { id: 'service', name: 'Service' },
    { id: 'penalty', name: 'Pénalité' },
    { id: 'other', name: 'Autre' },
  ];

  const members = membersData?.members || [];
  const totals = calculateTotal();

  if (showReceipt && generatedPayment) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white text-black p-8 rounded-lg shadow-lg">
          {/* Receipt Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">GYMIT</h1>
            <p className="text-gray-600">Reçu de Paiement — Salle de Musculation</p>
            <p className="text-sm text-gray-500 mt-2">
              {new Date().toLocaleDateString('fr-FR', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>

          {/* Receipt Details */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">Détails du Paiement</h3>
              <p><strong>N° Reçu:</strong> {generatedPayment.receiptNumber}</p>
              <p><strong>Date:</strong> {new Date(generatedPayment.paymentDate).toLocaleDateString('fr-FR')}</p>
              <p><strong>Mode de Paiement:</strong> {paymentMethods.find(m => m.id === generatedPayment.paymentMethod)?.name}</p>
              <p><strong>Statut:</strong> {generatedPayment.status === 'paid' ? 'Payé' : 'En attente'}</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">Informations Client</h3>
              <p><strong>Nom:</strong> {generatedPayment.memberName}</p>
              <p><strong>Code Membre:</strong> {generatedPayment.memberCode}</p>
              <p><strong>Catégorie:</strong> {categories.find(c => c.id === generatedPayment.category)?.name}</p>
            </div>
          </div>

          {/* Payment Details */}
          <div className="border-t border-gray-200 pt-4 mb-8">
            <h3 className="font-semibold text-gray-800 mb-4">Détails du Montant</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Montant de base:</span>
                <span>{generatedPayment.amount.toLocaleString()} DA</span>
              </div>
              {generatedPayment.discount > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>Remise:</span>
                  <span>-{generatedPayment.discount.toLocaleString()} DA</span>
                </div>
              )}
              {generatedPayment.tax > 0 && (
                <div className="flex justify-between">
                  <span>Taxes:</span>
                  <span>+{generatedPayment.tax.toLocaleString()} DA</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>Total Payé:</span>
                <span>{(generatedPayment.amount - generatedPayment.discount + generatedPayment.tax).toLocaleString()} DA</span>
              </div>
            </div>
          </div>

          {/* Description */}
          {generatedPayment.description && (
            <div className="mb-8">
              <h3 className="font-semibold text-gray-800 mb-2">Description</h3>
              <p className="text-gray-600">{generatedPayment.description}</p>
            </div>
          )}

          {/* Footer */}
          <div className="border-t border-gray-200 pt-4 text-center">
            <p className="text-sm text-gray-500">
              Merci pour votre paiement !<br/>
              Conservez ce reçu pour vos archives.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-center space-x-4 mt-8 no-print">
            <button
              onClick={handlePrintReceipt}
              className="flex items-center space-x-2 bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors"
            >
              <PrinterIcon className="h-5 w-5" />
              <span>Imprimer</span>
            </button>
            <button
              onClick={() => navigate('/payments')}
              className="flex items-center space-x-2 bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors"
            >
              <CheckCircleIcon className="h-5 w-5" />
              <span>Terminer</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 text-white hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">
              {isEditing ? 'Modifier le Paiement' : 'Nouveau Paiement'}
            </h1>
            <p className="text-white/60 mt-1">
              Enregistrez un paiement et générez un reçu
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Member Selection */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h3 className="text-xl font-semibold text-white mb-4">
            Informations du Membre
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-white font-medium mb-2">
                Membre *
              </label>
              <select
                name="memberId"
                value={formData.memberId}
                onChange={handleInputChange}
                className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Sélectionner un membre</option>
                {members.map(member => (
                  <option key={member.id} value={member.id}>
                    {member.firstName} {member.lastName} - {member.memberCode}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-white font-medium mb-2">
                Catégorie
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Payment Details */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h3 className="text-xl font-semibold text-white mb-4">
            Détails du Paiement
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-white font-medium mb-2">
                Montant (DA) *
              </label>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                placeholder="0"
                className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-white font-medium mb-2">
                Mode de Paiement
              </label>
              <div className="grid grid-cols-2 gap-2">
                {paymentMethods.map(method => (
                  <label
                    key={method.id}
                    className={`relative flex items-center space-x-2 p-3 rounded-xl border cursor-pointer transition-all ${
                      formData.paymentMethod === method.id
                        ? 'border-blue-500 bg-blue-500/20'
                        : 'border-white/20 bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={method.id}
                      checked={formData.paymentMethod === method.id}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${method.color}`}>
                      <method.icon className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-white text-sm font-medium">{method.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-white font-medium mb-2">
                Date de Paiement *
              </label>
              <input
                type="date"
                name="paymentDate"
                value={formData.paymentDate}
                onChange={handleInputChange}
                className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-white font-medium mb-2">
                Date d'Échéance
              </label>
              <input
                type="date"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleInputChange}
                className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-white font-medium mb-2">
                Remise (DA)
              </label>
              <input
                type="number"
                name="discount"
                value={formData.discount}
                onChange={handleInputChange}
                placeholder="0"
                className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-white font-medium mb-2">
                Taxes (DA)
              </label>
              <input
                type="number"
                name="tax"
                value={formData.tax}
                onChange={handleInputChange}
                placeholder="0"
                className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-white font-medium mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              placeholder="Description du paiement..."
              className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Summary */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h3 className="text-xl font-semibold text-white mb-4">
            Résumé du Paiement
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between text-white/80">
              <span>Montant de base:</span>
              <span>{(parseFloat(formData.amount) || 0).toLocaleString()} DA</span>
            </div>
            {totals.discount > 0 && (
              <div className="flex justify-between text-red-400">
                <span>Remise:</span>
                <span>-{totals.discount.toLocaleString()} DA</span>
              </div>
            )}
            {totals.tax > 0 && (
              <div className="flex justify-between text-white/80">
                <span>Taxes:</span>
                <span>+{totals.tax.toLocaleString()} DA</span>
              </div>
            )}
            <div className="flex justify-between text-xl font-bold text-white border-t border-white/20 pt-3">
              <span>Total à payer:</span>
              <span>{totals.total.toLocaleString()} DA</span>
            </div>
          </div>

          <div className="mt-6">
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                name="issueReceipt"
                checked={formData.issueReceipt}
                onChange={handleInputChange}
                className="rounded border-white/20 bg-white/10 text-blue-500 focus:ring-blue-500"
              />
              <span className="text-white">Générer un reçu</span>
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-white/10 text-white rounded-xl border border-white/20 hover:bg-white/20 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={createPaymentMutation.isLoading}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 flex items-center space-x-2"
          >
            {createPaymentMutation.isLoading && (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            )}
            <span>
              {isEditing ? 'Mettre à jour' : 'Enregistrer'} le Paiement
            </span>
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default PaymentForm; 